import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Activity, AlertTriangle, ArrowRight, CheckCircle2, ExternalLink, LifeBuoy,
  RefreshCw, TrendingDown, TrendingUp, Wallet, Building2, Zap, Gauge, WifiOff,
  History, BellRing, X,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../api";
import { PageHeader, eur } from "../components/common";
import { ConnectorCredentialsBanner } from "../components/ConnectorCredentialsBanner";
import { overlayCloseHandlers } from "../lib/overlayClose";

const KIND_LABEL = { dedicated: "Dedicado", dynamic: "Dinamico" };

const HEALTH_META = {
  up: { label: "Online", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
  slow: { label: "Lento", cls: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-500" },
  down: { label: "Offline", cls: "bg-red-50 text-red-700 border-red-200", dot: "bg-red-500" },
  auth_error: { label: "Auth", cls: "bg-red-50 text-red-700 border-red-200", dot: "bg-red-500" },
  missing_key: { label: "Sem chave", cls: "bg-slate-100 text-slate-600 border-slate-200", dot: "bg-slate-400" },
};

function Sparkline({ series, threshold }) {
  const pts = (series || []).filter((s) => s.latency_ms != null);
  if (pts.length === 0) return <div className="text-[10px] text-slate-400">Sem dados de latencia (24h)</div>;
  const max = Math.max(...pts.map((p) => p.latency_ms), threshold || 1);
  return (
    <div className="flex items-end gap-[2px] h-8" data-testid="health-sparkline">
      {pts.slice(-40).map((p, i) => {
        const h = Math.max(2, Math.round((p.latency_ms / max) * 32));
        const color = !p.ok ? "bg-red-400" : p.latency_ms >= (threshold || 1500) ? "bg-amber-400" : "bg-emerald-400";
        return <div key={i} className={`w-1 rounded-sm ${color}`} style={{ height: `${h}px` }}
          title={`${p.latency_ms} ms - ${new Date(p.created_at).toLocaleTimeString("pt-PT")}`} />;
      })}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, tone = "navy", testid }) {
  const tones = {
    navy: "border-l-navy text-navy",
    blue: "border-l-blue text-blue",
    green: "border-l-emerald-500 text-emerald-600",
    amber: "border-l-amber-500 text-amber-600",
    red: "border-l-red-500 text-red-600",
  };
  const cls = tones[tone] || tones.navy;
  return (
    <div data-testid={testid} className={`bg-white border border-tint border-l-4 ${cls.split(" ")[0]} rounded-xl p-5`}>
      <div className="flex items-start justify-between">
        <span className="text-muted2 text-[11px] uppercase tracking-[0.14em] font-semibold">{label}</span>
        <Icon size={18} className={cls.split(" ")[1]} />
      </div>
      <div className="mt-3 text-3xl font-bold tracking-tight text-navy">{value}</div>
      {sub && <div className="mt-1 text-xs text-muted2">{sub}</div>}
    </div>
  );
}

function StatusPill({ configured, syncStatus }) {
  if (!configured) {
    return <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200 uppercase tracking-wide font-semibold">Sem credenciais</span>;
  }
  if (syncStatus === "erro") {
    return <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-200 uppercase tracking-wide font-semibold">Sync falhou</span>;
  }
  if (syncStatus === "ok") {
    return <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 uppercase tracking-wide font-semibold">Saudavel</span>;
  }
  return <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 uppercase tracking-wide font-semibold">Configurado</span>;
}

export default function ConnectorsExecutivePanel() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [health, setHealth] = useState({});
  const [healthCfg, setHealthCfg] = useState({ slow_threshold_ms: 1500 });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [pinging, setPinging] = useState(false);
  const [history, setHistory] = useState(null); // {connector, platform_name, data} | null
  const [alertCfg, setAlertCfg] = useState(null); // config modal state | null
  const [savingCfg, setSavingCfg] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const [ep, hp] = await Promise.all([
        api.get("/connectors/executive-panel"),
        api.get("/connectors/health").catch(() => ({ data: { connectors: [] } })),
      ]);
      setData(ep.data);
      const map = {};
      (hp.data.connectors || []).forEach((c) => { map[c.connector] = c; });
      setHealth(map);
      setHealthCfg({ slow_threshold_ms: hp.data.slow_threshold_ms || 1500 });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const pingNow = async () => {
    setPinging(true);
    try {
      await api.post("/connectors/health/ping");
      toast.success("Ping de saude executado");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setPinging(false); }
  };

  const openHistory = async (p) => {
    setHistory({ connector: p.connector, platform_name: p.platform_name, data: null });
    try {
      const { data } = await api.get(`/connectors/health/history?connector=${p.connector}&days=14`);
      setHistory({ connector: p.connector, platform_name: p.platform_name, data });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setHistory(null);
    }
  };

  const openAlertCfg = async () => {
    try {
      const { data } = await api.get("/connectors/health/alert-config");
      setAlertCfg(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveAlertCfg = async () => {
    setSavingCfg(true);
    try {
      await api.put("/connectors/health/alert-config", alertCfg);
      toast.success("Configuracao de alertas guardada");
      setAlertCfg(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingCfg(false); }
  };

  const syncAll = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/dashboard/sync-all");
      const ok = (data.results || []).filter((r) => r.ok).length;
      const fail = (data.results || []).filter((r) => !r.ok).length;
      if (ok && !fail) toast.success(`Sincronizacao concluida (${data.total_synced} subscricoes)`);
      else if (ok && fail) toast.warning(`Sincronizacao parcial (${ok} OK, ${fail} falharam)`);
      else toast.error("Sincronizacao falhou em todos os conectores");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSyncing(false); }
  };

  if (loading) return <div className="text-sm text-muted2">A carregar...</div>;
  if (!data) return <div className="text-sm text-muted2">Sem dados.</div>;

  const t = data.totals || {};
  const platforms = data.platforms || [];
  const anyFailing = platforms.some((p) => p.sync_last_status === "erro");

  return (
    <div>
      <PageHeader
        overline="Integracoes e Infra"
        title="Painel de Plataformas"
        action={
          <div className="flex items-center gap-2">
            <button onClick={openAlertCfg} data-testid="exec-alert-config"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-tint text-navy text-sm font-semibold hover:bg-slate-200">
              <BellRing size={14} /> Alertas
            </button>
            <button onClick={pingNow} disabled={pinging} data-testid="exec-ping-now"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-tint text-navy text-sm font-semibold hover:bg-slate-200 disabled:opacity-50">
              <Gauge size={14} className={pinging ? "animate-spin" : ""} /> Ping de saude
            </button>
            <button onClick={syncAll} disabled={syncing} data-testid="exec-sync-all"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blue text-white text-sm font-semibold hover:bg-[var(--blue-hover)] disabled:opacity-50">
              <RefreshCw size={14} className={syncing ? "animate-spin" : ""} /> Sincronizar todas
            </button>
          </div>
        }
      />

      <ConnectorCredentialsBanner />

      {anyFailing && (
        <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
          className="mb-6 flex items-start gap-3 border border-red-200 bg-red-50 rounded-lg p-4" data-testid="exec-alert">
          <AlertTriangle className="text-red-600 mt-0.5 shrink-0" size={20} />
          <div className="flex-1">
            <div className="text-red-700 font-semibold text-sm">Sincronizacao com erro em uma ou mais plataformas</div>
            <div className="text-red-600 text-xs mt-0.5">Verifique as credenciais em cada plataforma e clique em "Sincronizar todas".</div>
          </div>
        </motion.div>
      )}

      {/* KPIs globais */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8" data-testid="exec-kpis">
        <Kpi icon={Wallet} label="MRR total" value={eur(t.mrr)} sub={`ARR ${eur(t.arr)}`} tone="blue" testid="kpi-mrr" />
        <Kpi icon={CheckCircle2} label="Ativas" value={t.ativas || 0} tone="green" testid="kpi-ativas" />
        <Kpi icon={TrendingDown} label="Suspensas" value={t.suspensas || 0} tone="amber" testid="kpi-suspensas" />
        <Kpi icon={TrendingDown} label="Canceladas" value={t.canceladas || 0} tone="red" testid="kpi-canceladas" />
        <Kpi icon={LifeBuoy} label="Tickets abertos" value={t.tickets_abertos || 0} tone={t.tickets_abertos > 0 ? "amber" : "navy"} testid="kpi-tickets" />
        <Kpi icon={Building2} label="Plataformas" value={platforms.length} sub={`${platforms.filter((p) => p.configured).length} configuradas`} tone="navy" testid="kpi-plataformas" />
      </div>

      {/* Grelha por plataforma */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="exec-platforms-grid">
        {platforms.map((p, i) => {
          const churnTone = p.churn_pct > 10 ? "red" : p.churn_pct > 5 ? "amber" : "green";
          return (
            <motion.div key={p.connector} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              data-testid={`exec-plat-${p.connector}`}
              className="bg-white border border-tint rounded-xl p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Zap size={16} className="text-blue" />
                    <span className="font-bold text-navy tracking-tight truncate">{p.platform_name}</span>
                  </div>
                  <div className="text-[10px] font-mono text-slate-500 mt-0.5">{p.connector}</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <StatusPill configured={p.configured} syncStatus={p.sync_last_status} />
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200 uppercase tracking-wide font-semibold">{KIND_LABEL[p.kind] || p.kind}</span>
                </div>
              </div>

              {/* Metricas */}
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="text-muted2 text-[10px] uppercase tracking-wide font-semibold">MRR</div>
                  <div className="text-navy text-xl font-bold tracking-tight">{eur(p.mrr)}</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="text-muted2 text-[10px] uppercase tracking-wide font-semibold">Churn</div>
                  <div className={`text-xl font-bold tracking-tight ${churnTone === "red" ? "text-red-600" : churnTone === "amber" ? "text-amber-600" : "text-emerald-600"}`}>
                    {p.churn_pct} %
                  </div>
                </div>
              </div>

              <div className="text-sm space-y-1 text-body mb-3">
                <div className="flex justify-between"><span className="text-muted2">Subs ativas</span><b>{p.subscricoes_ativas}</b></div>
                <div className="flex justify-between"><span className="text-muted2">Suspensas</span><b>{p.subscricoes_suspensas}</b></div>
                <div className="flex justify-between"><span className="text-muted2">Canceladas</span><b>{p.subscricoes_canceladas}</b></div>
                <div className="flex justify-between"><span className="text-muted2">Tickets abertos</span>
                  <b className={p.tickets_abertos > 0 ? "text-amber-600" : ""}>{p.tickets_abertos}</b>
                </div>
              </div>

              {/* Saude do conector (ping 5-5min) */}
              {(() => {
                const h = health[p.connector];
                const latest = h?.latest;
                const st = latest?.state || (p.configured ? null : "missing_key");
                const hm = HEALTH_META[st];
                return (
                  <div className="mb-3 bg-slate-50 rounded-lg p-3" data-testid={`exec-health-${p.connector}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-1.5">
                        <Gauge size={12} className="text-slate-500" />
                        <span className="text-muted2 text-[10px] uppercase tracking-wide font-semibold">Saude (24h)</span>
                      </div>
                      {hm ? (
                        <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border uppercase tracking-wide font-semibold ${hm.cls}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${hm.dot}`} /> {hm.label}
                        </span>
                      ) : (
                        <span className="text-[10px] text-slate-400">Sem ping ainda</span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div>
                        <div className="text-muted2 text-[9px] uppercase tracking-wide font-semibold">Latencia</div>
                        <div className="text-navy text-sm font-bold">{latest?.latency_ms != null ? `${latest.latency_ms} ms` : "-"}{h?.avg_latency_24h != null && <span className="text-[10px] text-muted2 font-normal"> (med {h.avg_latency_24h})</span>}</div>
                      </div>
                      <div>
                        <div className="text-muted2 text-[9px] uppercase tracking-wide font-semibold">Uptime</div>
                        <div className={`text-sm font-bold ${h?.uptime_24h != null && h.uptime_24h < 95 ? "text-amber-600" : "text-navy"}`}>{h?.uptime_24h != null ? `${h.uptime_24h}%` : "-"}</div>
                      </div>
                    </div>
                    <Sparkline series={h?.series} threshold={healthCfg.slow_threshold_ms} />
                    {latest && !latest.ok && latest.error && (
                      <div className="flex items-start gap-1 text-[10px] text-red-600 mt-2" title={latest.error}>
                        <WifiOff size={10} className="mt-0.5 shrink-0" />
                        <span className="line-clamp-2">{latest.error}</span>
                      </div>
                    )}
                    <button onClick={() => openHistory(p)} data-testid={`exec-history-${p.connector}`}
                      className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-blue hover:text-[var(--blue-hover)]">
                      <History size={12} /> Historico (14d)
                    </button>
                  </div>
                );
              })()}

              {/* Rodape com estado de sync */}
              <div className="text-[10px] text-muted2 border-t border-tint pt-2">
                {p.sync_last_run ? (
                  <>
                    <div className="flex items-center gap-1">
                      <Activity size={10} className={p.sync_last_status === "erro" ? "text-red-500" : "text-emerald-500"} />
                      Ultima sync: {new Date(p.sync_last_run).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" })} ({p.sync_last_count ?? 0})
                    </div>
                    {p.sync_last_status === "erro" && p.sync_last_error && (
                      <div className="text-red-600 mt-1 line-clamp-2" title={p.sync_last_error}>{p.sync_last_error}</div>
                    )}
                  </>
                ) : (
                  <span>Sem sincronizacoes registadas.</span>
                )}
              </div>

              {p.ui_path && (
                <button onClick={() => nav(p.ui_path)} data-testid={`exec-open-${p.connector}`}
                  className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-tint text-navy text-xs font-semibold hover:bg-blue hover:text-white transition-colors">
                  Abrir <ArrowRight size={12} />
                </button>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Modal Historico multi-dia */}
      {history && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="exec-history-modal"
          {...overlayCloseHandlers(() => setHistory(null))}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <History size={18} className="text-blue" />
                <h3 className="font-bold text-navy tracking-tight">Latencia e uptime - {history.platform_name}</h3>
              </div>
              <button onClick={() => setHistory(null)} data-testid="exec-history-close" className="p-1 rounded hover:bg-slate-100 text-slate-500"><X size={18} /></button>
            </div>
            {!history.data ? (
              <div className="text-sm text-muted2 py-8 text-center">A carregar...</div>
            ) : (history.data.buckets || []).length === 0 ? (
              <div className="text-sm text-muted2 py-8 text-center">Ainda nao ha historico suficiente para este conector.</div>
            ) : (
              <>
                {(() => {
                  const buckets = history.data.buckets;
                  const maxLat = Math.max(...buckets.map((b) => b.avg_latency || 0), history.data.slow_threshold_ms || 1);
                  return (
                    <div>
                      <div className="text-[11px] text-muted2 uppercase tracking-wide font-semibold mb-2">Latencia media diaria (ms)</div>
                      <div className="flex items-end gap-2 h-40 border-b border-tint pb-1">
                        {buckets.map((b) => {
                          const h = b.avg_latency ? Math.max(4, Math.round((b.avg_latency / maxLat) * 150)) : 2;
                          const color = b.uptime != null && b.uptime < 95 ? "bg-amber-400" : "bg-blue";
                          return (
                            <div key={b.date} className="flex-1 flex flex-col items-center justify-end gap-1" title={`${b.date}: ${b.avg_latency ?? "-"} ms media, ${b.max_latency ?? "-"} ms max, uptime ${b.uptime ?? "-"}%`}>
                              <span className="text-[9px] text-slate-500">{b.avg_latency ?? "-"}</span>
                              <div className={`w-full rounded-t ${color}`} style={{ height: `${h}px` }} />
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex gap-2 mt-1">
                        {buckets.map((b) => (
                          <div key={b.date} className="flex-1 text-center text-[9px] text-slate-400">{b.date.slice(5)}</div>
                        ))}
                      </div>
                      <div className="grid grid-cols-3 gap-3 mt-5">
                        {buckets.slice(-1).map((b) => (
                          <div key="today" className="bg-slate-50 rounded-lg p-3 col-span-3 md:col-span-1">
                            <div className="text-muted2 text-[10px] uppercase tracking-wide font-semibold">Uptime (dia)</div>
                            <div className={`text-xl font-bold ${b.uptime != null && b.uptime < 95 ? "text-amber-600" : "text-navy"}`}>{b.uptime != null ? `${b.uptime}%` : "-"}</div>
                          </div>
                        ))}
                        <div className="bg-slate-50 rounded-lg p-3 col-span-3 md:col-span-1">
                          <div className="text-muted2 text-[10px] uppercase tracking-wide font-semibold">Limite lento</div>
                          <div className="text-xl font-bold text-navy">{history.data.slow_threshold_ms} ms</div>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-3 col-span-3 md:col-span-1">
                          <div className="text-muted2 text-[10px] uppercase tracking-wide font-semibold">Dias com dados</div>
                          <div className="text-xl font-bold text-navy">{buckets.length}</div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal Config de alertas */}
      {alertCfg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="exec-alert-config-modal"
          {...overlayCloseHandlers(() => !savingCfg && setAlertCfg(null))}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-1">
              <BellRing size={18} className="text-blue" />
              <h3 className="font-bold text-navy tracking-tight">Alertas de conector offline</h3>
            </div>
            <p className="text-xs text-muted2 mb-4">Envia um email quando um conector fica offline por mais do que o tempo definido.</p>
            <label className="flex items-center gap-2 mb-4 text-sm text-navy font-medium">
              <input type="checkbox" data-testid="alertcfg-enabled" checked={alertCfg.enabled}
                onChange={(e) => setAlertCfg({ ...alertCfg, enabled: e.target.checked })} className="w-4 h-4" />
              Alertas ativos
            </label>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs font-semibold text-navy mb-1">Offline apos (min)</label>
                <input type="number" min={1} data-testid="alertcfg-offline-after" value={alertCfg.offline_after_min}
                  onChange={(e) => setAlertCfg({ ...alertCfg, offline_after_min: Number(e.target.value) })}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue/20 focus:outline-none" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-navy mb-1">Cooldown (min)</label>
                <input type="number" min={5} data-testid="alertcfg-cooldown" value={alertCfg.cooldown_min}
                  onChange={(e) => setAlertCfg({ ...alertCfg, cooldown_min: Number(e.target.value) })}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue/20 focus:outline-none" />
              </div>
            </div>
            <label className="block text-xs font-semibold text-navy mb-1">Destinatarios (emails, separados por virgula)</label>
            <input data-testid="alertcfg-recipients" value={alertCfg.recipients}
              onChange={(e) => setAlertCfg({ ...alertCfg, recipients: e.target.value })}
              placeholder="ops@cogniseg.com (vazio = super admins)" className="w-full mb-4 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue/20 focus:outline-none" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setAlertCfg(null)} disabled={savingCfg} data-testid="alertcfg-cancel"
                className="px-3 py-2 rounded-lg text-sm font-semibold text-navy bg-tint hover:bg-slate-200">Cancelar</button>
              <button onClick={saveAlertCfg} disabled={savingCfg} data-testid="alertcfg-save"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-white bg-blue hover:bg-[var(--blue-hover)] disabled:opacity-50">
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
